import { fetchAuth } from "../../../shared/api/ApiService";
import { IAuditRegularScheduleHistoryItem, IAuditStudentWalletHistoryItem, IAuditWalletHistoryFilter, TStudentWalletHistoryType } from "../model/types";

interface IAuditRefResponse {
    id?: string;
    userId?: string;
    fullName?: string;
    username?: string;
    title?: string;
    name?: string;
    tgAccountId?: string;
}

interface IAuditLocalDateResponse {
    year: number;
    month: number;
    day: number;
}

interface IAuditSlotPatternResponse {
    weekday: string;
    startSlot: number;
    durationSlots: number;
}

interface IAuditDateOverrideResponse {
    date?: IAuditLocalDateResponse;
    availableSlots?: {
        startsAt?: string;
        durationSlots?: number;
    }[];
    comment?: string;
}

interface IAuditHistoryItemResponse {
    scheduleId: string;
    student?: IAuditRefResponse;
    tutor?: IAuditRefResponse;
    tgAccount?: IAuditRefResponse;
    tag?: IAuditRefResponse;
    startsOn?: IAuditLocalDateResponse;
    endsOn?: IAuditLocalDateResponse;
    weeklySlots?: IAuditSlotPatternResponse[];
    status?: string;
    comment?: string;
    audit?: {
        createdAt?: string;
        createdByUserId?: string;
        updatedAt?: string;
        updatedByUserId?: string;
    };
    updatedByFullName?: string;
    updatedByRoleName?: string;
    source?: string;
    description?: string;
    action?: string;
    entityType?: string;
    dateOverrides?: IAuditDateOverrideResponse[];
}

interface IAuditStudentWalletHistoryItemResponse {
    id?: string | number;
    student?: IAuditRefResponse;
    oldBalance?: string | number;
    old_balance?: string | number;
    balanceBefore?: string | number;
    balance_before?: string | number;
    newBalance?: string | number;
    new_balance?: string | number;
    balanceAfter?: string | number;
    balance_after?: string | number;
    amount?: string | number;
    delta?: string | number;
    balanceDelta?: string | number;
    balance_delta?: string | number;
    comment?: string;
    description?: string;
    action?: string;
    type?: string;
    audit?: {
        createdAt?: string;
        createdByUserId?: string;
        createdByFullName?: string;
        createdByRoleName?: string;
        updatedAt?: string;
        updatedByUserId?: string;
        updatedByFullName?: string;
        updatedByRoleName?: string;
        created_at?: string;
        created_by_user_id?: string;
        created_by_full_name?: string;
        created_by_role_name?: string;
        updated_at?: string;
        updated_by_user_id?: string;
        updated_by_full_name?: string;
        updated_by_role_name?: string;
    };
    updatedByFullName?: string;
    updated_by_full_name?: string;
    createdByFullName?: string;
    created_by_full_name?: string;
    updatedByRoleName?: string;
    updated_by_role_name?: string;
    createdByRoleName?: string;
    created_by_role_name?: string;
}

interface IAuditWalletHistoryFilterResponse {
    type?: string;
    label?: string;
}

const readValue = (value: string | number | undefined): string => (
    value === undefined || value === null ? '' : String(value)
)

const readFirstText = (...values: (string | undefined)[]): string => (
    values.find(value => Boolean(value?.trim()))?.trim() ?? ''
)

const mapHistoryItem = (item: IAuditHistoryItemResponse): IAuditRegularScheduleHistoryItem => ({
    schedule_id: Number(item.scheduleId),
    student: item.student?.id || item.student?.fullName ? {
        id: item.student?.id ?? '',
        userId: item.student?.userId ?? '',
        fullName: item.student?.fullName ?? '',
        tgAccountId: item.student?.tgAccountId ?? '',
    } : undefined,
    tutor: item.tutor?.id || item.tutor?.fullName ? {
        id: item.tutor?.id ?? '',
        userId: item.tutor?.userId ?? '',
        fullName: item.tutor?.fullName ?? '',
        tgAccountId: item.tutor?.tgAccountId ?? '',
    } : undefined,
    tg_account: item.tgAccount?.id || item.tgAccount?.username || item.tgAccount?.title ? {
        id: item.tgAccount?.id ?? '',
        username: item.tgAccount?.username ?? '',
        title: item.tgAccount?.title ?? '',
    } : undefined,
    tag: item.tag?.id || item.tag?.name ? {
        id: item.tag?.id ?? '',
        name: item.tag?.name ?? '',
    } : undefined,
    starts_on: item.startsOn,
    ends_on: item.endsOn,
    weekly_slots: (item.weeklySlots ?? []).map(slot => ({
        weekday: slot.weekday as IAuditRegularScheduleHistoryItem['weekly_slots'][number]['weekday'],
        startSlot: Number(slot.startSlot),
        durationSlots: Number(slot.durationSlots),
    })),
    status: (item.status ?? 'REGULAR_SCHEDULE_STATUS_UNSPECIFIED') as IAuditRegularScheduleHistoryItem['status'],
    comment: item.comment ?? '',
    audit: item.audit ? {
        createdAt: item.audit.createdAt ?? '',
        createdByUserId: item.audit.createdByUserId ?? '',
        updatedAt: item.audit.updatedAt ?? '',
        updatedByUserId: item.audit.updatedByUserId ?? '',
    } : undefined,
    updated_by_full_name: item.updatedByFullName ?? '',
    updated_by_role_name: item.updatedByRoleName ?? '',
    source: item.source ?? '',
    description: item.description ?? '',
    action: item.action ?? '',
    entity_type: (item.entityType ?? 'REGULAR_SCHEDULE_HISTORY_ENTITY_TYPE_STUDENT') as IAuditRegularScheduleHistoryItem['entity_type'],
    date_overrides: (item.dateOverrides ?? [])
        .filter((override): override is IAuditDateOverrideResponse & {date: IAuditLocalDateResponse} => Boolean(override.date))
        .map(override => ({
            date: override.date,
            available_slots: (override.availableSlots ?? [])
                .filter(slot => Boolean(slot.startsAt) && Number(slot.durationSlots) > 0)
                .map(slot => ({
                    starts_at: slot.startsAt as string,
                    duration_slots: Number(slot.durationSlots),
                })),
            comment: override.comment ?? '',
        })),
})

const mapStudentWalletHistoryItem = (
    item: IAuditStudentWalletHistoryItemResponse,
    index: number
): IAuditStudentWalletHistoryItem => ({
    id: readValue(item.id) || String(index),
    student: item.student?.id || item.student?.fullName ? {
        id: item.student?.id ?? '',
        userId: item.student?.userId ?? '',
        fullName: item.student?.fullName ?? '',
        tgAccountId: item.student?.tgAccountId ?? '',
    } : undefined,
    balance_before: readValue(item.balanceBefore ?? item.balance_before ?? item.oldBalance ?? item.old_balance),
    balance_after: readValue(item.balanceAfter ?? item.balance_after ?? item.newBalance ?? item.new_balance),
    balance_delta: readValue(item.balanceDelta ?? item.balance_delta ?? item.amount ?? item.delta),
    description: item.description ?? '',
    action: item.action ?? '',
    type: (item.type ?? 'WALLET_HISTORY_TYPE_MANUAL_ADJUSTMENT') as TStudentWalletHistoryType,
    updated_by_full_name: readFirstText(
        item.updatedByFullName,
        item.updated_by_full_name,
        item.audit?.updatedByFullName,
        item.audit?.updated_by_full_name,
        item.createdByFullName,
        item.created_by_full_name,
        item.audit?.createdByFullName,
        item.audit?.created_by_full_name,
    ),
    updated_by_role_name: readFirstText(
        item.updatedByRoleName,
        item.updated_by_role_name,
        item.audit?.updatedByRoleName,
        item.audit?.updated_by_role_name,
        item.createdByRoleName,
        item.created_by_role_name,
        item.audit?.createdByRoleName,
        item.audit?.created_by_role_name,
    ),
    audit: item.audit ? {
        createdAt: item.audit.createdAt ?? item.audit.created_at ?? '',
        createdByUserId: readFirstText(item.audit.createdByUserId, item.audit.created_by_user_id),
        updatedAt: item.audit.updatedAt ?? item.audit.updated_at ?? '',
        updatedByUserId: readFirstText(item.audit.updatedByUserId, item.audit.updated_by_user_id),
    } : undefined,
})

class AuditService {
    async listStudentRegularScheduleHistory(studentId: number): Promise<IAuditRegularScheduleHistoryItem[]> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/audit/students/${studentId}/regular-schedule-history`)
        const data: {items?: IAuditHistoryItemResponse[]} = await res.json()
        return (data.items ?? []).map(mapHistoryItem)
    }

    async listStudentWalletHistory(studentId: number, types: TStudentWalletHistoryType[] = []): Promise<IAuditStudentWalletHistoryItem[]> {
        const params = new URLSearchParams()
        types.forEach(type => params.append('types', type))
        const query = params.toString()
        const res = await fetchAuth(
            `${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/audit/students/${studentId}/wallet-history${query ? `?${query}` : ''}`
        )
        const data: {items?: IAuditStudentWalletHistoryItemResponse[]} | IAuditStudentWalletHistoryItemResponse[] = await res.json()
        const items = Array.isArray(data) ? data : data.items ?? []
        return items.map(mapStudentWalletHistoryItem)
    }

    async listWalletHistoryFilters(): Promise<IAuditWalletHistoryFilter[]> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/audit/wallet-history/filters`)
        const data: {filters?: IAuditWalletHistoryFilterResponse[]} = await res.json()

        return (data.filters ?? [])
            .filter(filter => Boolean(filter.type) && Boolean(filter.label))
            .map(filter => ({
                type: filter.type as string,
                label: filter.label as string,
            }))
    }

    async listTutorRegularScheduleHistory(userId: number, studentIds: number[] = []): Promise<IAuditRegularScheduleHistoryItem[]> {
        const params = new URLSearchParams()
        studentIds.forEach(studentId => {
            if(studentId > 0){
                params.append('student_ids', String(studentId))
            }
        })

        const query = params.toString()
        const res = await fetchAuth(
            `${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/audit/tutors/${userId}/regular-schedule-history${query ? `?${query}` : ''}`
        )
        const data: {items?: IAuditHistoryItemResponse[]} = await res.json()
        return (data.items ?? []).map(mapHistoryItem)
    }
}

export const auditService = new AuditService()
